import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { shopPublicService } from '../../../services/shop-public.service';
import { StorefrontOrderStatusComponent } from '../components/storefront-order-status.component';
import type { OrderPublicStatus } from '../components/storefront-order-status.component';

interface RouteParams {
    uuid: string;
}

/**
 * StorefrontOrderStatusContainer
 *
 * Handles screen 5 of 5 in the public storefront flow:
 *   /shop/order/:uuid
 *
 * Responsibilities:
 * - Read :uuid from the route params (bookmarkable, no query string)
 * - Fetch shopPublicService.proformaStatus(uuid) on mount
 * - Handle loading / error / not-found (HTTP 404) states
 * - Provide a "refresh" affordance (manual re-fetch)
 *
 * SCOPE NOTE (V1): the public endpoint only returns STATUS by UUID.
 * Full line-item detail is not exposed publicly; guests receive the full
 * invoice and tickets by email after checkout. A richer public detail view
 * would require a new/extended public endpoint — deferred.
 *
 * No authentication required — registered as an OpenRoute.
 */
export const StorefrontOrderStatusContainer: React.FC = () => {
    const { uuid } = useParams<RouteParams>();

    const [status, setStatus] = useState<OrderPublicStatus | null>(null);
    const [isFetching, setIsFetching] = useState(false);
    const [isNotFound, setIsNotFound] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [invoiceDownloadUrl, setInvoiceDownloadUrl] = useState<string | null>(null);

    const fetchStatus = useCallback(async (cancelled?: { value: boolean }) => {
        if (!uuid) return;

        setIsFetching(true);
        setErrorMessage(undefined);

        try {
            const response = await shopPublicService.proformaStatus(uuid);
            if (!cancelled?.value) {
                setStatus(response.results.status);
                setInvoiceDownloadUrl(response.results.invoice?.downloadUrl ?? null);
                setIsNotFound(false);
            }
        } catch (e: any) {
            if (!cancelled?.value) {
                // Treat HTTP 404 / status 404 as "order not found"
                if (e?.status === 404 || e?.statusCode === 404) {
                    setIsNotFound(true);
                } else {
                    setErrorMessage(e?.errorMessage ?? e?.message ?? 'Failed to load order status.');
                }
            }
        } finally {
            if (!cancelled?.value) {
                setIsFetching(false);
            }
        }
    }, [uuid]);

    // Auto-fetch on mount
    useEffect(() => {
        const cancelled = { value: false };
        fetchStatus(cancelled);
        return () => {
            cancelled.value = true;
        };
    }, [fetchStatus]);

    const handleRefresh = useCallback(() => {
        if (!isFetching) {
            fetchStatus();
        }
    }, [fetchStatus, isFetching]);

    return (
        <StorefrontOrderStatusComponent
            uuid={uuid}
            status={status}
            isFetching={isFetching}
            isNotFound={isNotFound}
            errorMessage={errorMessage}
            invoiceDownloadUrl={invoiceDownloadUrl}
            onRefresh={handleRefresh}
        />
    );
};

export default StorefrontOrderStatusContainer;
